import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import type { AfterViewInit, EmbeddedViewRef } from '@angular/core';
import {
  afterNextRender,
  ApplicationRef,
  Component,
  computed,
  createComponent,
  DestroyRef,
  DOCUMENT,
  effect,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  Renderer2,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import H from '@here/maps-api-for-javascript';
import { chain, isEqual, pick, uniq } from 'lodash';
import { Duration } from 'luxon';
import {
  concat,
  connect,
  debounce,
  debounceTime,
  fromEvent,
  fromEventPattern,
  merge,
  skip,
  Subject,
  switchMap,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import {
  ONYX_LAYOUT_DEBOUNCE_TIME,
  ONYX_TOOLTIP_DELAY,
  ONYX_TRANSITION_DURATION,
} from '../../../constants';
import type { OnyxOption } from '../../../directives/interfaces';
import { OnyxDropdownDirective } from '../../../directives/onyx-dropdown.directive';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import { OnyxOverlayPosition, OnyxTheme } from '../../../enums';
import { MapHelper, UtilityHelper } from '../../../helpers';
import type { OnyxOptional } from '../../../interfaces';
import { OnyxBorderTooltipComponent } from '../../../internal/components/onyx-border-tooltip/onyx-border-tooltip.component';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxStorageKey } from '../../../internal/enums';
import { OnyxMapService } from '../../../internal/services/onyx-map.service';
import { OnyxMapEventQueue } from '../../../internal/utilities';
import { OnyxThemeService } from '../../../services/onyx-theme.service';
import type { OnyxCoordinates } from '../../address-input/interfaces';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import type { OnyxComponentInputs } from '../../table/interfaces';
import { getPolylineStyles, VEHICLE_RESTRICTIONS_FEATURE } from '../constants';
import type { OnyxMapVehicleStatus } from '../enums';
import {
  OnyxMapEventType,
  OnyxMapLayerType,
  OnyxMapMarkerType,
  OnyxMapMode,
  OnyxMapObjectZIndex,
  OnyxMapRoutePointStatus,
  OnyxMapRoutePointVariant,
  OnyxMapRouteVariant,
  OnyxMapVehicleVariant,
} from '../enums';
import type {
  OnyxMapBorderMarkerData,
  OnyxMapContextMenuData,
  OnyxMapContextMenuOptions,
  OnyxMapCustomRoute,
  OnyxMapFitContentFilters,
  OnyxMapMarkerData,
  OnyxMapRouteOptions,
  OnyxMapRoutePoint,
  OnyxMapRoutePointMarkerData,
  OnyxMapTooltip,
  OnyxMapTooltipData,
  OnyxMapVehicleMarkerData,
  OnyxMapVehicleOptions,
  OnyxMapVehicleSpecification,
  OnyxRoute,
  OnyxRouteBorder,
} from '../interfaces';
import { OnyxMapEvent } from '../interfaces';
import { OnyxMapContextMenuComponent } from '../onyx-map-context-menu/onyx-map-context-menu.component';

@Component({
  selector: 'onyx-map',
  imports: [
    OnyxIconComponent,
    OnyxIconButtonComponent,
    OnyxDropdownDirective,
    OnyxTooltipDirective,
    NgTemplateOutlet,
  ],
  templateUrl: './onyx-map.component.html',
  styleUrl: './onyx-map.component.scss',
  host: {
    '[class.border]': `border()`,
    '[class.minimized]': `mode() === OnyxMapMode.MINIMIZED`,
    '[class.maximized]': `mode() === OnyxMapMode.MAXIMIZED`,
    '[class.standalone]': `standalone()`,
    '[class.selected-vehicle]': `!!selectedVehiclesId()?.length`,
    '[class.show-only-selected-vehicle]': `showOnlySelectedVehicle()`,
  },
})
export class OnyxMapComponent implements AfterViewInit {
  private readonly injector = inject(Injector);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly mapService = inject(OnyxMapService);
  private readonly renderer = inject(Renderer2);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly applicationRef = inject(ApplicationRef);
  private readonly themeService = inject(OnyxThemeService);

  public readonly defer = input(false);
  public readonly preview = input(true);
  public readonly border = input(true);
  public readonly standalone = input(false);
  public readonly showOnlySelectedVehicle = input(false);
  public readonly showControls = input(true);
  public readonly maxSelectedVehiclesCount = input(1);
  public readonly contextMenuOptions =
    input<OnyxOptional<OnyxMapContextMenuOptions[]>>();
  public readonly vehicleSpecification =
    input<OnyxMapVehicleSpecification | null>(null);

  public readonly mapClick = output<void>();
  public readonly vehicleMarkerClick = output<string>();
  public readonly selectedVehiclesIdChange = output<string[]>();

  protected readonly I18N = `${I18N_NAMESPACE}.map`;

  protected readonly OnyxMapMode = OnyxMapMode;
  protected readonly OnyxOverlayPosition = OnyxOverlayPosition;

  protected readonly LAYER_TYPE_OPTIONS: OnyxOption<OnyxMapLayerType>[] =
    Object.values(OnyxMapLayerType).map((value) => ({
      name: `${this.I18N}.layerTypes.${value}`,
      value,
    }));

  private readonly CENTER_POINT = new H.geo.Point(
    47.45402532725695,
    9.131194340990346,
  );
  private readonly MIN_ZOOM = 4;
  private readonly INITIAL_ZOOM = 5;
  private readonly ZOOM_STEP = 0.5;
  private readonly ANIMATE_SPEED = 4;
  private readonly BEHAVIORS = [
    H.mapevents.Behavior.Feature.FRACTIONAL_ZOOM,
    H.mapevents.Behavior.Feature.PANNING,
    H.mapevents.Behavior.Feature.PINCH_ZOOM,
    H.mapevents.Behavior.Feature.WHEEL_ZOOM,
    H.mapevents.Behavior.Feature.ZOOM_MOMENTUM,
  ] as const;

  private readonly controlsElementRef =
    viewChild<ElementRef<HTMLElement>>('controlsElement');

  public mode = signal(OnyxMapMode.MINIMIZED);

  protected layerButtonActive = signal(false);
  protected layerType = signal(
    (() => {
      const storage = localStorage.getItem(OnyxStorageKey.MAP_LAYER_TYPE);
      if (UtilityHelper.isEnum(OnyxMapLayerType, storage)) return storage;
      return OnyxMapLayerType.DEFAULT;
    })(),
  );

  protected showPreview = computed(
    () => this.mode() === OnyxMapMode.MINIMIZED && this.preview(),
  );

  protected selectedVehiclesId = signal<string[]>([]);

  private map = signal<H.Map | null>(null);
  private vehicleSpecificationValue = linkedSignal({
    source: this.vehicleSpecification,
    computation: (specification) => specification,
    equal: isEqual,
  });

  private behaviors: H.mapevents.Behavior | null = null;
  private ui: H.ui.UI | null = null;

  private _vehicles = new Map<string, OnyxMapVehicleMarkerData>();
  public get vehicles(): MapIterator<string> {
    return this._vehicles.keys();
  }

  private selectedVehicleMarkers = new Map<string, H.map.DomMarker>();
  private vehiclesCluster: H.clustering.Provider | null = null;
  private vehiclesClusterUpdate$ = new Subject<{ force: boolean }>();

  private _routes = new Map<
    number,
    {
      polylines: H.map.Polyline[];
      borders: H.map.DomMarker[];
      compareData: unknown;
      redraw: () => void;
    }
  >();

  public get routes(): MapIterator<number> {
    return this._routes.keys();
  }

  private _routePoints = new Map<string, H.map.DomMarker>();
  public get routePoints(): MapIterator<string> {
    return this._routePoints.keys();
  }

  private _markers = new Map<string, H.map.Marker>();
  public get markers(): MapIterator<string> {
    return this._markers.keys();
  }

  private queue = new OnyxMapEventQueue();
  private tooltipDetach$ = new Subject<void>();
  private tooltipData = new Map<string, OnyxMapTooltipData>();
  private contextMenu = signal<OnyxMapContextMenuData | null>(null);

  private isDarkTheme = computed(
    () => this.themeService.theme() === OnyxTheme.DARK,
  );

  constructor() {
    effect(() =>
      localStorage.setItem(OnyxStorageKey.MAP_LAYER_TYPE, this.layerType()),
    );

    effect(() => {
      const max = this.maxSelectedVehiclesCount();

      untracked(() => {
        const selectedVehiclesId = this.selectedVehiclesId();
        if (selectedVehiclesId.length <= max) return;

        const limitedVehiclesId = selectedVehiclesId.slice(0, max);

        this.dispatch(
          new OnyxMapEvent.SelectVehicles({ ids: limitedVehiclesId }),
        );
        this.selectedVehiclesIdChange.emit(limitedVehiclesId);
      });
    });

    effect(() => {
      if (!this.map()) return;

      if (this.showPreview()) this.dispatch(new OnyxMapEvent.FitContent());
      setTimeout(() => this.resizeMap());
    });

    const defaultLayers = this.mapService.getDefaultLayers();
    const { normal, traffic } = defaultLayers.vector;
    const CUSTOM_LAYERS = {
      traffic: traffic.map,
      trafficNight: traffic.mapnight,
    };

    effect(() => {
      const map = this.map();
      if (!map) return;

      const layerType = this.layerType();
      const isDarkTheme = this.isDarkTheme();

      let baseLayer: H.map.layer.TileLayer;
      const customLayers: H.map.layer.TileLayer[] = [];

      switch (layerType) {
        case OnyxMapLayerType.DEFAULT:
          baseLayer = isDarkTheme ? normal.mapnight : normal.map;
          break;
        case OnyxMapLayerType.SATELLITE:
          baseLayer = defaultLayers.raster.satellite.map;
          break;
        case OnyxMapLayerType.TRAFFIC:
          baseLayer = isDarkTheme ? normal.mapnight : normal.map;
          customLayers.push(
            isDarkTheme ? CUSTOM_LAYERS.trafficNight : CUSTOM_LAYERS.traffic,
          );
          break;
        case OnyxMapLayerType.RESTRICTIONS:
          baseLayer = isDarkTheme ? normal.logisticsnight : normal.logistics;
          break;
      }

      baseLayer.setMin(this.MIN_ZOOM);
      map.setBaseLayer(baseLayer);

      for (const layer of Object.values(CUSTOM_LAYERS)) {
        customLayers.includes(layer)
          ? map.addLayer(layer)
          : map.removeLayer(layer);
      }

      this.updateVehicleRestrictions(
        map,
        untracked(() => this.vehicleSpecificationValue()),
      );
    });

    effect(() => {
      const map = this.map();
      if (!map) return;

      this.updateVehicleRestrictions(map, this.vehicleSpecificationValue());
    });

    effect(() => {
      this.isDarkTheme();
      untracked(() => {
        for (const { redraw } of [...this._routes.values()]) redraw();
      });
    });

    effect(() => {
      if (!this.behaviors) return;

      this.toggleBehaviors(!this.showPreview());
    });
  }

  public ngAfterViewInit(): void {
    this.updateControlsDirection();

    timer(this.defer() ? ONYX_TRANSITION_DURATION : 0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showMap());
  }

  public dispatch(event: OnyxMapEvent): void {
    this.queue.add(event);
  }

  protected zoomIn(): void {
    const map = this.map();
    if (!map) return;

    map
      .getViewModel()
      .setLookAtData(
        { zoom: map.getZoom() + this.ZOOM_STEP },
        this.ANIMATE_SPEED,
      );
  }

  protected zoomOut(): void {
    const map = this.map();
    if (!map) return;

    map
      .getViewModel()
      .setLookAtData(
        { zoom: map.getZoom() - this.ZOOM_STEP },
        this.ANIMATE_SPEED,
      );
  }

  @HostListener('document:keydown.escape')
  protected minimizeMap(): void {
    if (this.mode() !== OnyxMapMode.MAXIMIZED) return;
    this.mode.set(OnyxMapMode.MINIMIZED);
  }

  private toggleBehaviors(enable = true): void {
    for (const behavior of this.BEHAVIORS) {
      enable
        ? this.behaviors?.enable(behavior)
        : this.behaviors?.disable(behavior);
    }

    const element = this.map()?.getViewPort().element as HTMLElement;
    this.renderer.setStyle(element, 'pointer-events', enable ? 'all' : 'none');
  }

  private updateVehicleRestrictions(
    map: H.Map,
    vehicleSpecification: OnyxMapVehicleSpecification | null,
  ): void {
    const provider = map.getBaseLayer()?.getProvider();
    if (!provider || !(provider instanceof H.service.omv.Provider)) return;

    const style = provider.getStyle() as H.map.render.harp.Style | null;
    if (!style) return;

    const enabledFeatures = style.getEnabledFeatures() ?? [];
    const otherFeatures = enabledFeatures.filter(
      ({ feature }) => feature !== VEHICLE_RESTRICTIONS_FEATURE,
    );

    if (!vehicleSpecification) {
      style.setEnabledFeatures(otherFeatures);
      provider.setVehicleSpecs(null);

      return;
    }

    style.setEnabledFeatures([
      ...otherFeatures,
      { feature: VEHICLE_RESTRICTIONS_FEATURE, mode: 'active & inactive' },
    ]);
    provider.setVehicleSpecs(vehicleSpecification);
  }

  private showMap(): void {
    const defaultLayers = this.mapService.getDefaultLayers();
    const { normal } = defaultLayers.vector;
    const map = new H.Map(
      this.elementRef.nativeElement,
      this.isDarkTheme() ? normal.mapnight : normal.map,
      {
        center: this.CENTER_POINT,
        zoom: this.INITIAL_ZOOM,
        pixelRatio: window.devicePixelRatio || 1,
        padding: {
          top: 0,
          right: this.standalone() ? 32 : 0,
          bottom: 0,
          left: this.standalone() ? 640 : 0,
        },
      },
    );
    this.map.set(map);

    this.behaviors = new H.mapevents.Behavior(new H.mapevents.MapEvents(map));

    const ui = H.ui.UI.createDefault(map, defaultLayers);
    ui.removeControl('zoom');
    ui.removeControl('mapsettings');
    ui.removeControl('scalebar');
    this.ui = ui;

    this.createVehiclesCluster();

    map.addEventListener('pointermove', (event: H.mapevents.Pointer): void => {
      let isActive = false;
      if (event.target instanceof H.map.AbstractMarker) {
        isActive = event.target.getData()?.callback != null;
      } else if (event.target instanceof H.map.Polyline) {
        isActive = true;
      }

      this.renderer.setStyle(
        this.elementRef.nativeElement,
        'cursor',
        isActive ? 'pointer' : 'auto',
      );
    });

    map.addEventListener('tap', (event: H.mapevents.Pointer): void => {
      const isRightClick =
        ((event as any).originalEvent as PointerEvent)?.button === 2;
      if (isRightClick) return;

      const isMarker = event.target instanceof H.map.AbstractMarker;
      const isPolyline = event.target instanceof H.map.Polyline;
      if (isMarker || isPolyline) return;

      this.mapClick.emit();
    });

    map.addEventListener('dbltap', (event: H.mapevents.Pointer): void => {
      const isRightClick =
        ((event as any).originalEvent as PointerEvent)?.button === 2;
      if (isRightClick) return;

      this.dispatch(new OnyxMapEvent.FitContent());
    });

    map.addEventListener(
      'contextmenu',
      (event: H.mapevents.ContextMenuEvent): void =>
        this.openContextMenu(event.viewportX, event.viewportY),
    );

    map.addEventListener('mapviewchange', (): void => this.closeContextMenu());

    this.vehiclesClusterUpdate$
      .pipe(
        debounceTime(1),
        connect((shared$) =>
          concat(
            shared$.pipe(take(1)),
            shared$.pipe(
              debounce((update) =>
                timer(
                  update.force
                    ? 0
                    : Duration.fromObject({ seconds: 5 }).toMillis(),
                ),
              ),
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.forceUpdateVehiclesCluster());

    fromEventPattern<void>(
      (handler) => {
        const resizeObserver = new ResizeObserver(() => handler());
        resizeObserver.observe(this.elementRef.nativeElement);

        map.addOnDisposeCallback(() => resizeObserver?.disconnect());
        return resizeObserver;
      },
      (_, resizeObserver?: ResizeObserver) => resizeObserver?.disconnect(),
    )
      .pipe(
        skip(1),
        debounceTime(ONYX_LAYOUT_DEBOUNCE_TIME),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.resizeMap());

    setTimeout(() =>
      this.queue.events$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event) => this.processEvent(event)),
    );
  }

  private processEvent(event: OnyxMapEvent): void {
    switch (event.TYPE) {
      case OnyxMapEventType.ADD_UPDATE_VEHICLE:
        return this.addUpdateVehicle(
          event.id,
          event.status,
          event.coordinates,
          event.azimuth,
          event.options,
        );
      case OnyxMapEventType.REMOVE_VEHICLE:
        return this.removeVehicle(event.id);
      case OnyxMapEventType.SELECT_VEHICLES:
        return this.selectVehicles(event.ids);

      case OnyxMapEventType.ADD_UPDATE_ROUTE:
        return this.addUpdateRoute(
          event.id,
          event.route,
          event.points,
          event.options,
          event.callback,
        );
      case OnyxMapEventType.ADD_UPDATE_CUSTOM_ROUTE:
        return this.addUpdateCustomRoute(
          event.id,
          event.route,
          event.options,
          event.callback,
        );
      case OnyxMapEventType.REMOVE_ROUTE:
        return this.removeRoute(event.id);

      case OnyxMapEventType.ADD_UPDATE_ROUTE_POINT:
        return this.addUpdateRoutePoint(event.point, event.callback);
      case OnyxMapEventType.REMOVE_ROUTE_POINT:
        return this.removeRoutePoint(event.id);

      case OnyxMapEventType.ADD_UPDATE_MARKER:
        return this.addUpdateMarker(
          event.id,
          event.type,
          event.coordinates,
          event.callback,
          event.tooltip,
          event.active,
        );
      case OnyxMapEventType.REMOVE_MARKER:
        return this.removeMarker(event.id);

      case OnyxMapEventType.FIT_CONTENT:
        return this.fitContent(event.filters);
    }
  }

  private addUpdateVehicle(
    id: string,
    status: OnyxMapVehicleStatus,
    coordinates: OnyxOptional<OnyxCoordinates>,
    azimuth: OnyxOptional<number>,
    options?: OnyxMapVehicleOptions,
  ): void {
    const DATA_COMPARE_KEYS: (keyof OnyxMapVehicleMarkerData)[] = [
      'status',
      'coordinates',
      'azimuth',
    ];
    const OPTIONS_COMPARE_KEYS: (keyof OnyxMapVehicleOptions)[] = [
      'variant',
      'hidden',
    ];

    const data: OnyxMapVehicleMarkerData = {
      id,
      status,
      coordinates,
      azimuth: MapHelper.getVehicleStatusAzimuth(status, azimuth) ?? 0,
      options,
    };

    const previousData = this._vehicles.get(id);
    const isSame =
      isEqual(
        pick(previousData, DATA_COMPARE_KEYS),
        pick(data, DATA_COMPARE_KEYS),
      ) &&
      isEqual(
        pick(previousData?.options, OPTIONS_COMPARE_KEYS),
        pick(data.options, OPTIONS_COMPARE_KEYS),
      );

    this._vehicles.set(id, data);
    if (!data.coordinates) return;

    if (this.selectedVehiclesId()?.includes(id)) {
      const marker = this.selectedVehicleMarkers.get(id);
      if (!marker) return;

      marker.setData(data);
      this.addUpdateMarkerTooltip(id, marker, options?.tooltip);
      if (isSame) return;

      marker.setGeometry({
        lat: data.coordinates.latitude,
        lng: data.coordinates.longitude,
      });
      marker.setIcon(this.createVehicleIcon(data, { selected: true }));
    } else {
      if (isSame) return;

      this.vehiclesClusterUpdate$.next({
        force:
          !previousData ||
          !isEqual(
            pick(previousData.options, OPTIONS_COMPARE_KEYS),
            pick(data.options, OPTIONS_COMPARE_KEYS),
          ),
      });
    }
  }

  private removeVehicle(id: string): void {
    this.removeMarkerTooltip(id);
    if (!this._vehicles.has(id)) return;

    this._vehicles.delete(id);
    this.vehiclesClusterUpdate$.next({ force: true });
  }

  private selectVehicles(ids: string[]): void {
    const newIds = uniq(ids)
      .slice(-this.maxSelectedVehiclesCount())
      .filter((id) => this._vehicles.has(id));
    if (isEqual(this.selectedVehiclesId(), newIds)) return;

    this.selectedVehiclesId.set(newIds);
    this.selectedVehiclesIdChange.emit(newIds);

    this.clearSelectedVehicleMarkers();

    for (const id of newIds) {
      const data = this._vehicles.get(id);
      if (!data?.coordinates || data.options?.hidden) continue;

      const marker = new H.map.DomMarker(
        { lat: data.coordinates.latitude, lng: data.coordinates.longitude },
        {
          data,
          icon: this.createVehicleIcon(data, { selected: true }),
          zIndex: OnyxMapObjectZIndex.SELECTED_VEHICLE,
        },
      );

      marker.addEventListener('tap', () =>
        this.dispatch(new OnyxMapEvent.FitContent()),
      );
      this.addUpdateMarkerTooltip(id, marker, data.options?.tooltip);

      this.map()?.addObject(marker);
      this.selectedVehicleMarkers.set(id, marker);
    }

    this.vehiclesClusterUpdate$.next({ force: true });
  }

  private addUpdateRoutePoint(
    point: OnyxMapRoutePoint,
    callback: () => void,
  ): void {
    const { coordinates, id, tooltip } = point;

    const geometry: H.geo.IPoint = {
      lat: coordinates?.latitude ?? 0,
      lng: coordinates?.longitude ?? 0,
    };
    const data: OnyxMapRoutePointMarkerData = { point, callback };
    const zIndex =
      point.zIndex ??
      (point.active
        ? OnyxMapObjectZIndex.ROUTE_POINT_ACTIVE
        : OnyxMapObjectZIndex.ROUTE_POINT);

    let marker = this._routePoints.get(id);
    if (marker) {
      const previousData = marker.getData() as OnyxMapRoutePointMarkerData;
      if (!isEqual(previousData, data)) {
        marker.setGeometry(geometry);
        marker.setData(data);
        this.updateRoutePointIcon(marker);
        marker.setVisibility(!!coordinates);
        marker.setZIndex(zIndex);
        marker.removeEventListener('tap', previousData.callback);
        marker.addEventListener('tap', () => callback());
        this.addUpdateMarkerTooltip(id, marker, tooltip);
      }
      return;
    }

    marker = new H.map.DomMarker(geometry, {
      data,
      icon: this.createRoutePointIcon(id, point),
      visibility: !!coordinates,
      zIndex,
    });
    marker.addEventListener('tap', () => callback());
    this.addUpdateMarkerTooltip(id, marker, tooltip);

    this.map()!.addObject(marker);
    this._routePoints.set(id, marker);
  }

  private removeRoutePoint(id: string): void {
    const marker = this._routePoints.get(id);
    this.removeMarkerTooltip(id);
    if (!marker) return;

    this._routePoints.delete(id);
    this.map()!.removeObject(marker);
  }

  private addUpdateRoute(
    id: number,
    route: OnyxRoute,
    points: OnyxMapRoutePoint[],
    options: OnyxMapRouteOptions | undefined,
    callback: (index: number) => void,
  ): void {
    const compareData = this.getRouteCompareData(route, points, options);
    if (this.isRouteUnchanged(id, compareData)) return;

    if (this._routes.has(id)) this.removeRoute(id);

    const polylines: H.map.Polyline[] = [];
    const borders: H.map.DomMarker[] = [];

    const routePoints = points.filter((point) => point.coordinates != null);
    const activeSections = new Set<number>();

    let lastIncludedActive: number | null = null;
    for (const [index, point] of routePoints.entries()) {
      const isIncluded =
        point.included || index === 0 || index === routePoints.length - 1;
      if (point.active && isIncluded) {
        if (lastIncludedActive !== null) {
          for (let i = lastIncludedActive + 1; i <= index; ++i) {
            activeSections.add(i);
          }
        }
        lastIncludedActive = index;
      } else if (isIncluded) {
        lastIncludedActive = null;
      }
    }

    if (activeSections.size === 0 && routePoints[0]?.active) {
      activeSections.add(0);
    }

    for (const [pointIndex, point] of routePoints.entries()) {
      const legs = route.points[pointIndex].legs;

      for (const [legIndex, leg] of legs.entries()) {
        const active = activeSections.has(pointIndex);
        const geometries = chain(null)
          .thru((): { geometry: H.geo.LineString; completed: boolean }[] => {
            if (point.legProgress && point.legProgress.index === legIndex) {
              return [
                { geometry: point.legProgress.completed, completed: true },
                { geometry: point.legProgress.remaining, completed: false },
              ];
            }

            return [
              {
                geometry: H.geo.LineString.fromFlexiblePolyline(leg.polyline),
                completed: point.status === OnyxMapRoutePointStatus.COMPLETED,
              },
            ];
          })
          .filter(({ geometry }) => geometry.getPointCount() > 1)
          .value();

        for (const { geometry, completed } of geometries) {
          const styles = getPolylineStyles(
            {
              active,
              compliance: point.compliance,
              style: point.empty ? 'empty' : leg.type,
              completed,
              variant: options?.variant,
            },
            this.document,
          );
          for (const style of styles) {
            const polyline = new H.map.Polyline(geometry, {
              data: undefined,
              style,
              zIndex: active
                ? OnyxMapObjectZIndex.ROUTE_ACTIVE
                : OnyxMapObjectZIndex.ROUTE,
            });
            polyline.addEventListener('tap', () => callback(pointIndex));
            polylines.push(polyline);
          }
        }

        if (
          point.status !== OnyxMapRoutePointStatus.COMPLETED &&
          options?.variant !== OnyxMapRouteVariant.ALTERNATIVE
        ) {
          for (const [index, passthrough] of leg.passthroughs.entries()) {
            const { latitude, longitude } = passthrough.coordinates;

            const data: OnyxMapBorderMarkerData = {
              id: `border-${id}-${pointIndex}-${index}`,
              routeId: id,
              pointIndex,
              index,
              border: passthrough,
              type: options?.compact && !active ? 'dot' : 'pin',
              active,
              callback: () => callback(pointIndex),
            };

            const marker = new H.map.DomMarker(
              { lat: latitude, lng: longitude },
              {
                data,
                icon: this.createRoutePointIcon(data.id, {
                  style: data.type,
                  ...passthrough,
                }),
                zIndex: active
                  ? OnyxMapObjectZIndex.BORDER_POINT_ACTIVE
                  : OnyxMapObjectZIndex.BORDER_POINT,
              },
            );

            this.addUpdateMarkerTooltip(
              data.id,
              marker,
              {
                ref: OnyxBorderTooltipComponent,
                inputs:
                  (): OnyxComponentInputs<OnyxBorderTooltipComponent> => ({
                    border: passthrough,
                  }),
              },
              `border-${data.type}`,
            );

            marker.addEventListener('tap', () => callback(pointIndex));
            borders.push(marker);
          }
        }
      }
    }

    this._routes.set(id, {
      polylines,
      borders,
      compareData,
      redraw: () => this.addUpdateRoute(id, route, points, options, callback),
    });
    this.map()!.addObjects([...polylines, ...borders]);
  }

  private addUpdateCustomRoute(
    id: number,
    route: OnyxMapCustomRoute,
    options: OnyxMapRouteOptions | undefined,
    callback: () => void,
  ): void {
    const compareData = { route, options, isDarkTheme: this.isDarkTheme() };
    if (this.isRouteUnchanged(id, compareData)) return;

    if (this._routes.has(id)) this.removeRoute(id);

    const polylines: H.map.Polyline[] = [];

    const geometry = H.geo.LineString.fromLatLngArray(
      route.coordinates
        .map(({ latitude, longitude }) => [latitude, longitude])
        .flat(),
    );
    const styles = getPolylineStyles(
      { ...route, variant: options?.variant },
      this.document,
    );

    for (const style of styles) {
      const polyline = new H.map.Polyline(geometry, {
        data: undefined,
        style,
        zIndex: route.active
          ? OnyxMapObjectZIndex.ROUTE_ACTIVE
          : OnyxMapObjectZIndex.ROUTE,
      });
      polyline.addEventListener('tap', () => callback());
      polylines.push(polyline);
    }

    this._routes.set(id, {
      polylines,
      borders: [],
      compareData,
      redraw: () => this.addUpdateCustomRoute(id, route, options, callback),
    });
    this.map()!.addObjects(polylines);
  }

  private removeRoute(id: number): void {
    const route = this._routes.get(id);
    if (!route) return;

    for (const border of route.borders) {
      const data = border.getData() as OnyxMapBorderMarkerData;
      this.removeMarkerTooltip(data.id);
    }

    this._routes.delete(id);
    this.map()!.removeObjects([...route.polylines, ...route.borders]);
  }

  private getRouteCompareData(
    route: OnyxRoute,
    points: OnyxMapRoutePoint[],
    options: OnyxMapRouteOptions | undefined,
  ) {
    return {
      route,
      options,
      isDarkTheme: this.isDarkTheme(),
      points: points.map(({ tooltip: _tooltip, legProgress, ...point }) => ({
        ...point,
        legProgress: legProgress && {
          index: legProgress.index,
          completedPoints: legProgress.completed.getPointCount(),
          remainingPoints: legProgress.remaining.getPointCount(),
        },
      })),
    };
  }

  private isRouteUnchanged(id: number, compareData: unknown): boolean {
    return isEqual(this._routes.get(id)?.compareData, compareData);
  }

  private addUpdateMarker(
    id: string,
    type: OnyxMapMarkerType,
    coordinates: OnyxCoordinates,
    callback: (() => void) | undefined,
    tooltip: OnyxMapTooltip<OnyxMapMarkerData> | undefined,
    active: boolean | undefined,
  ): void {
    const createIcon = () => {
      const containerElement = this.renderer.createElement('div');
      this.renderer.setAttribute(containerElement, 'class', 'point-marker');

      const wrapperElement = this.renderer.createElement('div');
      this.renderer.setAttribute(
        wrapperElement,
        'class',
        `wrapper ${active ? 'active' : ''}`,
      );

      const iconName = `${type}${type === OnyxMapMarkerType.POINT && active ? '-active' : ''}`;
      const iconElement = this.renderer.createElement('img');
      this.renderer.setAttribute(
        iconElement,
        'src',
        `map/markers/${iconName}.svg`,
      );

      this.renderer.appendChild(wrapperElement, iconElement);
      this.renderer.appendChild(containerElement, wrapperElement);

      return new H.map.DomIcon(containerElement);
    };

    const geometry: H.geo.IPoint = {
      lat: coordinates.latitude,
      lng: coordinates.longitude,
    };
    const data: OnyxMapMarkerData = { id, type, coordinates, callback };

    let marker = this._markers.get(id);
    if (marker) {
      const previousData = marker.getData() as OnyxMapMarkerData;
      if (!isEqual(previousData, data)) {
        marker.setGeometry(geometry);
        marker.setData(data);
        marker.setIcon(createIcon());

        if (previousData.callback) {
          marker.removeEventListener('tap', previousData.callback);
        }
        if (callback) {
          marker.addEventListener('tap', callback);
        }

        this.addUpdateMarkerTooltip(id, marker, tooltip);
      }
      return;
    }

    marker = new H.map.DomMarker(geometry, {
      data,
      icon: createIcon(),
      zIndex: OnyxMapObjectZIndex.MARKER,
    });
    if (callback) marker.addEventListener('tap', callback);
    this.addUpdateMarkerTooltip(id, marker, tooltip);

    this._markers.set(id, marker);
    this.map()!.addObject(marker);
  }

  private removeMarker(id: string): void {
    const marker = this._markers.get(id);
    this.removeMarkerTooltip(id);
    if (!marker) return;

    this._markers.delete(id);
    this.map()!.removeObject(marker);
  }

  private addUpdateMarkerTooltip(
    id: string,
    marker: H.map.Marker,
    tooltip?: OnyxMapTooltip<any>,
    className?: string,
  ): void {
    const tooltipData = this.tooltipData.get(id);
    tooltipData?.subscription.unsubscribe();

    if (!tooltip) return this.removeMarkerTooltip(id);

    const markerEnter$ = fromEventPattern<void>(
      (handler) => marker.addEventListener('pointerenter', handler),
      (handler) => marker.removeEventListener('pointerenter', handler),
    );
    const markerLeave$ = fromEventPattern<void>(
      (handler) => marker.addEventListener('pointerleave', handler),
      (handler) => marker.removeEventListener('pointerleave', handler),
    );

    const removeBubble = () => {
      const tooltipData = this.tooltipData.get(id);

      if (tooltipData?.bubble) this.ui?.removeBubble(tooltipData.bubble);
      if (tooltipData?.componentRef) {
        this.applicationRef.detachView(tooltipData.componentRef.hostView);
        tooltipData?.componentRef.destroy();
      }

      this.tooltipData.set(id, {
        subscription,
        bubble: null,
        componentRef: null,
      });
    };

    const subscription = markerEnter$
      .pipe(
        switchMap(() =>
          timer(ONYX_TOOLTIP_DELAY).pipe(takeUntil(markerLeave$)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.tooltipDetach$.next();
        removeBubble();

        const componentRef = createComponent(tooltip.ref, {
          environmentInjector: this.environmentInjector,
        });
        this.applicationRef.attachView(componentRef.hostView);

        const inputs = tooltip.inputs(marker.getData());
        for (const [key, value] of Object.entries(inputs)) {
          componentRef.setInput(key, value);
        }
        componentRef.changeDetectorRef.detectChanges();

        const bubble = new H.ui.InfoBubble(marker.getGeometry() as H.geo.Point);
        bubble.addClass('tooltip');
        if (className) bubble.addClass(className);
        bubble.setContent(
          (componentRef.hostView as EmbeddedViewRef<any>)
            .rootNodes[0] as HTMLElement,
        );

        this.ui?.addBubble(bubble);
        this.tooltipData.set(id, { subscription, bubble, componentRef });

        const tooltipElement = bubble
          .getElement()!
          .querySelector('.H_ib_body')!;
        const tooltipEnter$ = fromEvent(tooltipElement, 'mouseenter');
        const tooltipLeave$ = fromEvent(tooltipElement, 'mouseleave');

        const enter$ = merge(markerEnter$, tooltipEnter$);
        const leave$ = merge(markerLeave$, tooltipLeave$);

        leave$
          .pipe(
            switchMap(() => timer(100).pipe(takeUntil(enter$))),
            take(1),
            takeUntil(this.tooltipDetach$),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(removeBubble);

        this.tooltipDetach$
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe(removeBubble);
      });

    this.tooltipData.set(id, {
      subscription,
      bubble: this.tooltipData.get(id)?.bubble ?? null,
      componentRef: this.tooltipData.get(id)?.componentRef ?? null,
    });
  }

  private removeMarkerTooltip(id: string): void {
    const tooltipData = this.tooltipData.get(id);
    if (!tooltipData) return;

    tooltipData.subscription.unsubscribe();
    if (tooltipData.bubble) this.ui?.removeBubble(tooltipData.bubble);
    tooltipData.componentRef?.destroy();

    this.tooltipData.delete(id);
  }

  private fitContent(filters?: OnyxMapFitContentFilters): void {
    const map = this.map();
    if (!map) return;

    const isSingleFilter = filters?.vehicleId || filters?.routePointId;
    const boundingBoxes: OnyxOptional<H.geo.Rect>[] = [];
    const coordinates: OnyxOptional<OnyxCoordinates>[] = [];

    if (filters?.vehicleId) {
      const vehicle = this._vehicles.get(filters.vehicleId);
      if (vehicle?.coordinates && !vehicle.options?.hidden) {
        coordinates.push(vehicle.coordinates);
      }
    } else if (filters?.routePointId) {
      boundingBoxes.push(
        this._routePoints
          .get(filters.routePointId)
          ?.getGeometry()
          .getBoundingBox(),
      );
    } else {
      const selectedIds = this.selectedVehiclesId();
      if (selectedIds?.length) {
        for (const id of selectedIds) {
          const vehicle = this._vehicles.get(id);
          if (!vehicle?.coordinates || vehicle.options?.hidden) continue;

          coordinates.push(vehicle.coordinates);
        }
      } else {
        for (const vehicle of this._vehicles.values()) {
          if (!vehicle.coordinates || vehicle.options?.hidden) continue;

          coordinates.push(vehicle.coordinates);
        }
      }

      boundingBoxes.push(
        ...Array.from(this._routes.values())
          .flatMap((route) => [...route.polylines, ...route.borders])
          .filter((object) => object.getVisibility())
          .map((object) => object.getGeometry().getBoundingBox()),
        ...Array.from(this._routePoints.values())
          .filter((object) => object.getVisibility())
          .map((object) => object.getGeometry().getBoundingBox()),
        ...Array.from(this._markers.values())
          .filter((object) => object.getVisibility())
          .map((object) => object.getGeometry().getBoundingBox()),
      );
    }

    boundingBoxes.push(
      ...coordinates.map((coordinates) => {
        if (!coordinates) return null;

        const { latitude, longitude } = coordinates;
        return new H.geo.Point(latitude, longitude).getBoundingBox();
      }),
    );

    const validBoundingBoxes = boundingBoxes.filter(
      (boundingBox) => boundingBox != null,
    );
    if (!validBoundingBoxes.length) {
      map
        .getViewModel()
        .setLookAtData(
          { position: this.CENTER_POINT, zoom: this.INITIAL_ZOOM },
          this.ANIMATE_SPEED,
        );
      return;
    }

    const boundingBox = validBoundingBoxes.reduce((result, box) =>
      result.mergeRect(box),
    );
    const horizontalPadding = Math.max(
      isSingleFilter ? 0.01 : 5,
      0.03 * boundingBox.getWidth(),
    );
    const verticalPadding = Math.max(
      isSingleFilter ? 0.01 : 5,
      0.03 * boundingBox.getHeight(),
    );

    const viewBox = new H.geo.Rect(
      boundingBox.getTop() + verticalPadding,
      boundingBox.getLeft() - horizontalPadding,
      boundingBox.getBottom() - verticalPadding,
      boundingBox.getRight() + horizontalPadding,
    );

    map.getViewModel().setLookAtData({ bounds: viewBox }, this.ANIMATE_SPEED);
  }

  private createVehiclesCluster(): void {
    const clusteringProvider = new H.clustering.Provider([], {
      clusteringOptions: {
        eps: 40,
        minWeight: 2,
      },
      theme: {
        getClusterPresentation: (cluster) => {
          const containerElement = this.renderer.createElement('div');
          this.renderer.setAttribute(
            containerElement,
            'class',
            'vehicles-cluster',
          );

          for (const threshold of [5, 10, 100]) {
            if (cluster.getWeight() >= threshold) continue;

            this.renderer.addClass(containerElement, `under-${threshold}`);
            break;
          }

          if (cluster.getWeight() <= 4) {
            cluster.forEachDataPoint((dataPoint) => {
              const data = dataPoint.getData() as OnyxMapVehicleMarkerData;

              const dotElement = this.renderer.createElement('div');
              this.renderer.setAttribute(
                dotElement,
                'class',
                `dot ${data.status}`,
              );
              UtilityHelper.toggleClass(
                this.renderer,
                dotElement,
                'small',
                cluster.getWeight() > 2,
              );
              this.renderer.appendChild(containerElement, dotElement);
            });
          } else {
            const countElement = this.renderer.createElement('h4');
            this.renderer.setAttribute(countElement, 'class', 'count');
            this.renderer.setProperty(
              countElement,
              'textContent',
              this.decimalPipe.transform(cluster.getWeight()),
            );
            this.renderer.appendChild(containerElement, countElement);
          }

          const marker = new H.map.DomMarker(cluster.getPosition(), {
            data: null,
            icon: new H.map.DomIcon(containerElement),
            min: cluster.getMinZoom(),
            max: cluster.getMaxZoom(),
            zIndex: OnyxMapObjectZIndex.VEHICLE,
          });

          marker.addEventListener('tap', () =>
            this.map()
              ?.getViewModel()
              .setLookAtData(
                {
                  position: cluster.getPosition(),
                  zoom: cluster.getMaxZoom() + 1,
                },
                this.ANIMATE_SPEED,
              ),
          );

          return marker;
        },
        getNoisePresentation: (noisePoint) => {
          const data = noisePoint.getData() as OnyxMapVehicleMarkerData;
          const { id, options } = data;

          const marker = new H.map.DomMarker(noisePoint.getPosition(), {
            data,
            icon: this.createVehicleIcon(data, { selected: false }),
            min: noisePoint.getMinZoom(),
            zIndex: OnyxMapObjectZIndex.VEHICLE,
          });

          this.addUpdateMarkerTooltip(id, marker, options?.tooltip);

          marker.addEventListener('tap', () =>
            this.vehicleMarkerClick.emit(id),
          );

          return marker;
        },
      },
    });
    const clusteringLayer = new H.map.layer.ObjectLayer(clusteringProvider);

    this.vehiclesCluster = clusteringProvider;
    this.map()?.addLayer(clusteringLayer);
  }

  private clearSelectedVehicleMarkers(): void {
    for (const [id, marker] of this.selectedVehicleMarkers) {
      this.removeMarkerTooltip(id);
      this.map()?.removeObject(marker);
      marker.dispose();
    }

    this.selectedVehicleMarkers.clear();
  }

  private forceUpdateVehiclesCluster(): void {
    const selectedVehiclesId = this.selectedVehiclesId();

    this.vehiclesCluster?.setDataPoints(
      Array.from(this._vehicles.values())
        .filter(
          (data) =>
            data.coordinates &&
            !data.options?.hidden &&
            !selectedVehiclesId.includes(data.id),
        )
        .map(
          (data) =>
            new H.clustering.DataPoint(
              data.coordinates!.latitude,
              data.coordinates!.longitude,
              undefined,
              data,
            ),
        ),
    );
  }

  private createVehicleIcon(
    data: OnyxMapVehicleMarkerData,
    { selected }: { selected: boolean },
  ): H.map.DomIcon {
    const { status, azimuth, options } = data;
    const variant = options?.variant ?? OnyxMapVehicleVariant.STATUS;

    const containerElement = this.renderer.createElement('div');
    this.renderer.setAttribute(
      containerElement,
      'class',
      `vehicle ${status} ${variant}`,
    );
    UtilityHelper.toggleClass(
      this.renderer,
      containerElement,
      'selected',
      selected,
    );
    UtilityHelper.toggleClass(
      this.renderer,
      containerElement,
      'hidden',
      !!options?.hidden,
    );

    const markerElement = this.renderer.createElement('div');
    this.renderer.setAttribute(markerElement, 'class', 'marker');
    this.renderer.appendChild(containerElement, markerElement);

    const iconElement = this.renderer.createElement('div');
    this.renderer.setAttribute(iconElement, 'class', 'icon');
    this.renderer.setStyle(
      iconElement,
      'transform',
      `translate(-50%, -50%) rotate(${azimuth}deg)`,
    );
    this.renderer.appendChild(markerElement, iconElement);

    return new H.map.DomIcon(containerElement);
  }

  private createRoutePointIcon(
    id: string,
    point: Pick<OnyxMapRoutePoint, 'style' | 'variant'> &
      (OnyxMapRoutePoint | OnyxRouteBorder),
  ): H.map.DomIcon {
    const isBorder = point.type === 'border';
    const variant = point.variant ?? OnyxMapRoutePointVariant.PRIMARY;

    const containerElement = this.renderer.createElement('div');
    this.renderer.setAttribute(
      containerElement,
      'class',
      `route-point ${point.style} ${point.type} ${variant}`,
    );

    if (!isBorder) {
      this.renderer.addClass(containerElement, point.status);
      if (point.compliance) {
        this.renderer.addClass(containerElement, point.compliance);
      }

      UtilityHelper.toggleClass(
        this.renderer,
        containerElement,
        'proposition',
        point.proposition,
      );

      UtilityHelper.toggleClass(
        this.renderer,
        containerElement,
        'active',
        point.active,
      );

      this.renderer.setAttribute(containerElement, 'id', id);
    }

    const markerElement = this.renderer.createElement('div');
    this.renderer.setAttribute(markerElement, 'class', 'marker');
    this.renderer.appendChild(containerElement, markerElement);

    const contentElement = this.renderer.createElement('div');
    this.renderer.setAttribute(
      contentElement,
      'class',
      'content f-paragraph-m f-semibold',
    );
    this.renderer.appendChild(markerElement, contentElement);

    if (!isBorder && !point.hideAction) {
      const actionElement = this.renderer.createElement('div');
      this.renderer.setAttribute(actionElement, 'class', 'action');
      this.renderer.appendChild(markerElement, actionElement);
    }

    if (isBorder) {
      this.renderer.setStyle(
        contentElement,
        'background-image',
        `url('flags/${point.to}.svg')`,
      );
    } else if (point.style === 'letter') {
      this.renderer.setProperty(
        contentElement,
        'textContent',
        point.letter ?? '?',
      );
    } else if (point.style === 'icon') {
      this.renderer.setStyle(
        contentElement,
        'mask-image',
        `url('icons/${point.icon ?? 'placeholder-outlined'}.svg')`,
      );
    } else {
      this.renderer.setStyle(
        contentElement,
        'mask-image',
        `url('icons/${MapHelper.getPointIconByType(point.type)}.svg')`,
      );
    }

    return new H.map.DomIcon(containerElement);
  }

  private updateRoutePointIcon(marker: H.map.DomMarker): void {
    const { point, callback } = marker.getData() as OnyxMapRoutePointMarkerData;
    const variant = point.variant ?? OnyxMapRoutePointVariant.PRIMARY;

    let containerElement: HTMLElement | null = null;
    let contentElement: HTMLElement | null = null;

    setTimeout(() =>
      afterNextRender(
        {
          earlyRead: () => {
            containerElement = this.elementRef.nativeElement.querySelector(
              `.route-point[id="${point.id}"]`,
            );
            contentElement =
              containerElement?.querySelector('.content') ?? null;
          },
          write: () => {
            if (!containerElement || !contentElement) {
              this.removeRoutePoint(point.id);
              this.addUpdateRoutePoint(point, callback);
              return;
            }

            this.renderer.setAttribute(
              containerElement,
              'class',
              `route-point ${point.style} ${point.type} ${point.status} ${variant}`,
            );
            if (point.compliance) {
              this.renderer.addClass(containerElement, point.compliance);
            }

            UtilityHelper.toggleClass(
              this.renderer,
              containerElement,
              'proposition',
              point.proposition,
            );

            UtilityHelper.toggleClass(
              this.renderer,
              containerElement,
              'active',
              point.active,
            );

            this.renderer.setProperty(contentElement, 'textContent', null);
            this.renderer.setStyle(contentElement, 'mask-image', '');

            if (point.style === 'letter') {
              this.renderer.setProperty(
                contentElement,
                'textContent',
                point.letter ?? '?',
              );
            } else if (point.style === 'icon') {
              this.renderer.setStyle(
                contentElement,
                'mask-image',
                `url('icons/${point.icon ?? 'placeholder-outlined'}.svg')`,
              );
            } else {
              this.renderer.setStyle(
                contentElement,
                'mask-image',
                `url('icons/${MapHelper.getPointIconByType(point.type)}.svg')`,
              );
            }
          },
        },
        { injector: this.injector },
      ),
    );
  }

  private resizeMap(): void {
    this.map()!.getViewPort().resize();
    this.updateControlsDirection();
  }

  private openContextMenu(viewportX: number, viewportY: number): void {
    this.closeContextMenu();

    const point = this.map()?.screenToGeo(viewportX, viewportY);
    if (!point) return;

    const componentRef = createComponent(OnyxMapContextMenuComponent, {
      environmentInjector: this.environmentInjector,
    });
    this.applicationRef.attachView(componentRef.hostView);

    componentRef.setInput('coordinates', {
      latitude: point.lat,
      longitude: point.lng,
    } satisfies OnyxCoordinates);
    componentRef.setInput('options', this.contextMenuOptions());
    componentRef.setInput('close', () => this.closeContextMenu());
    componentRef.changeDetectorRef.detectChanges();

    const bubble = new H.ui.InfoBubble(point);
    bubble.addClass('context-menu');
    bubble.setContent(
      (componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement,
    );

    this.ui?.addBubble(bubble);
    this.contextMenu.set({ bubble, componentRef });
  }

  private closeContextMenu(): void {
    const contextMenu = this.contextMenu();
    if (!contextMenu) return;

    this.ui?.removeBubble(contextMenu.bubble);
    this.applicationRef.detachView(contextMenu.componentRef.hostView);
    contextMenu.componentRef.destroy();

    this.contextMenu.set(null);
  }

  private updateControlsDirection(): void {
    const CONTROLS_PADDING = 8;

    const controlsElement = this.controlsElementRef()?.nativeElement;
    if (!controlsElement) return;

    const mapHeight = this.elementRef.nativeElement.offsetHeight;
    const controlsSize =
      Math.max(controlsElement.offsetHeight, controlsElement.offsetWidth) +
      2 * CONTROLS_PADDING;

    UtilityHelper.toggleClass(
      this.renderer,
      controlsElement,
      'column',
      controlsSize < mapHeight,
    );
  }
}
